/**
 * Report Builder — shared helpers: the blank builder state (report name, date
 * range), the selection check, breakdown dimension labels and value formatting
 * for the preview.
 */
// ── Default state ────────────────────────────────────────────────────────
/** "YYYY-MM-DD" in the user's local calendar — toISOString would give UTC's date. */
const isoLocal = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
/**
 * Default range: month to date up to yesterday (1st → yesterday). On the 1st
 * there is no yesterday in this month, so it falls back to the whole previous
 * month - the same rule the schedule uses for MTD.
 */
const defaultMtdRange = () => {
    const now = new Date();
    if (now.getDate() === 1) {
        return {
            start: isoLocal(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
            end: isoLocal(new Date(now.getFullYear(), now.getMonth(), 0)),
        };
    }
    return {
        start: isoLocal(new Date(now.getFullYear(), now.getMonth(), 1)),
        end: isoLocal(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)),
    };
};
/**
 * Placeholder name a brand new report starts with, so Add is never blocked on
 * naming before anything else can be done. The field is pre-selected on focus,
 * making it one gesture to replace.
 */
export const DEFAULT_REPORT_NAME = "Untitled Report";
/** Blank builder state — this month so far (1st → yesterday), broken down by day, nothing selected. */
export const createDefaultBuilderState = () => {
    const { start, end } = defaultMtdRange();
    return {
        report_name: DEFAULT_REPORT_NAME,
        template_id: "tpl_blank",
        network_keys: [],
        column_order: [],
        dimension_ids: ["day"],
        filters_by_network: {},
        breakdowns_by_network: {},
        enum_filters_by_network: {},
        source_by_network: {},
        custom_metrics: [],
        period_start: start,
        period_end: end,
        compare_start: null,
        compare_end: null,
    };
};
// ── Breakdown dimensions ─────────────────────────────────────────────────
/** Backend REPORT_DIMENSIONS keys and labels — the keys go to GET /client-config as-is. */
export const DIMENSION_LABELS = {
    day: "Day",
    week: "Week",
    month: "Month",
    campaign: "Campaign",
    publisher: "Publisher",
    age: "Age",
};
/** Document field each breakdown groups by, as the query summary names it. */
const DIMENSION_FIELDS = {
    day: "date",
    week: "week",
    month: "month",
    campaign: "campaign_name",
    publisher: "publisher_name",
};
// ── Selection check + query summary ──────────────────────────────────────
const quote = (value) => `'${value.replace(/'/g, "\\'")}'`;
const OPERATOR_SQL = {
    contains: "LIKE",
    does_not_contain: "NOT LIKE",
};
const filterClause = (filter) => {
    const value = filter.values[0]?.trim();
    if (!value)
        return null;
    return `${filter.field_id} ${OPERATOR_SQL[filter.operator]} ${quote(`%${value}%`)}`;
};
/**
 * Whether the selection can be generated yet, and the query it stands for. The
 * `isValid` / `message` pair gates Generate; `text` lists the networks, columns,
 * breakdowns, filters and date range.
 */
export const buildQuerySummary = (state) => {
    if (state.network_keys.length === 0) {
        return { text: "", isValid: false, message: "Select at least one network" };
    }
    if (state.column_order.length === 0) {
        return { text: "", isValid: false, message: "Add at least one column" };
    }
    // Column ids straight from the selection, e.g. "meta.spend".
    const columns = state.column_order;
    const fields = state.dimension_ids.map((id) => DIMENSION_FIELDS[id]).filter((x) => Boolean(x));
    const lines = [`FROM ${state.network_keys.join(", ")}`];
    lines.push(`  SHOW ${columns.join(", ")}`);
    if (fields.length > 0) {
        lines.push(`  BY ${fields.join(", ")}`);
    }
    const where = Object.values(state.filters_by_network)
        .flat()
        .filter((x) => Boolean(x))
        .map(filterClause)
        .filter((x) => Boolean(x));
    if (where.length > 0)
        lines.push(`  WHERE ${where.join(" AND ")}`);
    lines.push(`  SINCE ${state.period_start} UNTIL ${state.period_end}`);
    if (state.compare_start && state.compare_end) {
        lines.push(`  COMPARE TO ${state.compare_start} .. ${state.compare_end}`);
    }
    if (fields.length > 0 && columns.length > 0) {
        lines.push(`  ORDER BY ${columns[0]} DESC`);
    }
    return { text: lines.join("\n"), isValid: true, message: "Ready to generate" };
};
// ── Value formatting ─────────────────────────────────────────────────────
const compactNumber = (value) => {
    const abs = Math.abs(value);
    if (abs >= 1000000000)
        return `${(value / 1000000000).toFixed(1)}B`;
    if (abs >= 1000000)
        return `${(value / 1000000).toFixed(1)}M`;
    if (abs >= 10000)
        return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
};
/**
 * Render a raw value for display. `compact` is for axis labels and summary
 * tiles, where "1.2M" beats "1,238,402".
 */
export const formatValue = (value, format, compact = false) => {
    if (value == null || Number.isNaN(value))
        return "—";
    switch (format) {
        case "currency":
            return compact
                ? `$${compactNumber(value)}`
                : `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        case "percent":
            return `${value.toFixed(2)}%`;
        case "ratio":
            return value.toFixed(2);
        case "duration": {
            // Stored as minutes.
            const minutes = Math.floor(value);
            const seconds = Math.round((value - minutes) * 60);
            return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
        }
        case "number":
            return compact
                ? compactNumber(value)
                : value.toLocaleString(undefined, { maximumFractionDigits: 0 });
        default:
            return String(value);
    }
};
/**
 * Signed percentage change, or `null` when there is nothing to compare to.
 * 0 against 0 is no change - 0%, not "nothing to compare" - so that cell keeps
 * the same layout as its neighbours.
 */
export const percentChange = (current, previous) => {
    if (previous == null || Number.isNaN(previous))
        return null;
    if (previous === 0 && current === 0)
        return 0;
    // Growth from nothing has no true percentage; the portal reads it as 100%
    // (same as the backend's calculatePercentageDiff), down for a negative value.
    if (previous === 0)
        return Number.isNaN(current) ? null : Math.sign(current) * 100;
    return ((current - previous) / Math.abs(previous)) * 100;
};
